import MaterialButton from "@material-ui/core/Button";
import { styled as materialStyled } from "@material-ui/core/styles";
import { forwardRef } from "react";
import styled from "styled-components";
import { colours, darkerColours } from "../../styles/colours";
import { Icon, IconType } from "../icons";

const IconWrapper = styled.div`
  margin-right: 0.5em;
  display: flex;
  font-size: 1.2em;

  & > svg {
    width: 0.8em;
    height: 0.8em;
  }
`;

export type CustomMaterialButtonProps = React.ComponentProps<
  typeof CustomMaterialButton
>;

export type ButtonColour =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "warning";

const hoverActiveStyled = ({
  variant,
  colour,
}: {
  variant?: "outlined" | "contained" | "text";
  colour?: ButtonColour;
}) => ({
  boxShadow: "none",
  backgroundColor: (() => {
    if (variant !== "contained") return "transparent";
    switch (colour) {
      case "secondary":
        return darkerColours.blue;
      case "warning":
        return darkerColours.yellow;
      case "success":
        return darkerColours.green;
      case "danger":
        return darkerColours.red;
      default:
        return darkerColours.primaryBlue;
    }
  })(),
});

const CustomMaterialButton = materialStyled(MaterialButton)<
  null,
  React.ComponentProps<typeof MaterialButton> & {
    colour?: ButtonColour;
    variant?: "outlined" | "contained" | "text";
    active?: boolean;
  }
>(
  ({
    colour = "primary",
    variant = "contained",
    disabled,
    active = false,
  }) => ({
    textTransform: "none",
    borderRadius: "2rem",
    paddingLeft: "1.2em",
    paddingRight: "1.2em",
    fontWeight: 400,

    color: (() => {
      if (disabled) {
        return "rgb(201, 201, 201)";
      } else if (variant === "contained") {
        switch (colour) {
          case "secondary":
            return colours.white;
          case "warning":
            return colours.black;
          case "success":
            return colours.white;
          case "danger":
            return colours.white;
          default:
            return colours.white;
        }
      } else {
        switch (colour) {
          case "secondary":
            return colours.primaryBlue;
          case "warning":
            return colours.yellow;
          case "success":
            return colours.green;
          case "danger":
            return colours.red;
          default:
            return colours.blue;
        }
      }
    })(),

    backgroundColor: (() => {
      if (disabled) {
        return "rgba(0, 0, 0, 0.12)";
      }
      if (variant !== "contained") return "transparent";
      switch (colour) {
        case "secondary":
          return colours.primaryBlue;
        case "warning":
          return colours.yellow;
        case "success":
          return colours.green;
        case "danger":
          return colours.red;
        default:
          return colours.blue;
      }
    })(),

    borderColor: (() => {
      if (variant !== "outlined") return "none";
      if (disabled) {
        return "rgba(0, 0, 0, 0.12)";
      }
      switch (colour) {
        case "secondary":
          return colours.primaryBlue;
        case "warning":
          return colours.yellow;
        case "success":
          return colours.green;
        case "danger":
          return colours.red;
        default:
          return colours.blue;
      }
    })(),

    "&:hover": hoverActiveStyled({ variant, colour }),
    ...(active ? hoverActiveStyled({ variant, colour }) : {}),
  })
);

export type ButtonProps = CustomMaterialButtonProps & {
  icon?: IconType;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ icon, children, ...props }, ref) => {
    return (
      <CustomMaterialButton {...props} ref={ref}>
        {icon ? (
          <IconWrapper>
            <Icon type={icon} />
          </IconWrapper>
        ) : null}

        {children}
      </CustomMaterialButton>
    );
  }
);

export const PrimaryButton = (props: ButtonProps) => (
  <Button {...props} colour="primary" />
);
export const InfoButton = (props: ButtonProps) => (
  <Button {...props} colour="secondary" />
);
export const SuccessButton = (props: ButtonProps) => (
  <Button {...props} colour="success" />
);
export const DangerButton = (props: ButtonProps) => (
  <Button {...props} colour="danger" />
);
